import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {CloseIcon, Divider, IconButton, Layout, Text} from '@phonectas/unifon-ui-kit';
import {StyledCustomDialog} from 'common/components/UpsellDialog/styled';
import {StyledResDialogContent, StyledResDialogHeader} from './styled';
import MainResContent from './components/MainResContent';
import {TransferList} from './components/TransferList';
import {toStrictApi} from './toStrictApi';
import {VismaRestkontoDownloadRequest} from 'common/types/store/threeSixtyTypes';


interface VismaResAccDialogProps {
    id: string;
    title: string;
    onClose: () => void;
    show: boolean;
    width?: number;
    noOverflow?: boolean;
    maxHeight?: number;
    noPadding?: boolean;
    noPaddingHeader?: boolean;
    accountName: string;
    onDownloadReskonto: ({data}: {data: VismaRestkontoDownloadRequest}) => void;
	billingGroups?: string[];
	clientIds?: number[];
}

// interface ValueTypes {
// 	value: string | number | string[] | number[] | boolean | undefined | null;
// }

export interface VismaResAccState {
    account_id: number | null;
    format: 'excel' | 'csv';
    include_all: boolean;
    includes?: string[] | null;
    item_id?: number | null;
    use_entire_account: boolean;
    client_number?: number | null;
    isError?: boolean;
}

export interface StriktVismaResAccState extends VismaResAccState {
    account_id: number;
    format: 'excel' | 'csv';
    include_all: boolean;
    use_entire_account: boolean;
    includes: string[] | null;
}

const VismaResAccDialog: React.FC<VismaResAccDialogProps> = ({
	id,
	show,
	onClose,
	title,
	width,
	maxHeight,
	noOverflow = false,
	noPadding = false,
	noPaddingHeader = false,
	accountName = '',
	onDownloadReskonto,
	billingGroups = [],
	clientIds = [],
}) => {
	const [t] = useTranslation();
	const accountIdToInteger = useMemo(() => {
		return id ? parseInt(id as string, 10) : null;
	}, [id]);
	const [strictData, setStrictData] = useState<VismaRestkontoDownloadRequest | null>(null);
	const [shouldDownload, setShouldDownload] = useState(false);

	const DEFAULT_TEMP_RES_ACC = useMemo<VismaResAccState>(() => ({
		account_id: accountIdToInteger,
		format: 'excel',
		use_entire_account: true,
		include_all: true,
		includes: null,
	}), [accountIdToInteger]);

	const [tempResAcc, setTempResAcc] = useState<VismaResAccState>(DEFAULT_TEMP_RES_ACC);

	useEffect(() => {
		if (shouldDownload && strictData) {
			onDownloadReskonto({data: strictData});
			setShouldDownload(false);
		}
	}, [strictData, shouldDownload, onDownloadReskonto]);

	const handleTempResAccChange = useCallback(
		(field: string, value: string | number | boolean | string[] | number[] | null | undefined) => {
			setTempResAcc((prev) => ({
				...prev,
				[field]: value,
			}));
		},
		[]
	);

	const handleTransferList = useCallback((list: string[]) => {
		setTempResAcc((prev) => ({
			...prev,
			includes: list,
		}));
	}, []);

	const [showSideRes, setShowSideRes] = useState(false);
	const onShowSideRes = useCallback(() => {
		setShowSideRes(true);
		handleTempResAccChange('includes', []);
	}, [handleTempResAccChange]);
	const onHideSideRes = useCallback(() => {
		setShowSideRes(false);
		handleTempResAccChange('includes', null);
	}, [handleTempResAccChange]);
	const isAccountName = accountName !== '';

	const handleChange = useCallback(
		(field: keyof VismaResAccState, value: string | number | boolean | string[] | number[] | null | undefined) => {
			handleTempResAccChange(field, value);
		},
		[handleTempResAccChange]
	);

	const handleDownloadReskonto = useCallback(() => {
		if (!strictData) return;

		onDownloadReskonto({data: strictData});
	}, [onDownloadReskonto, strictData]);

	useEffect(() => {
		if (!tempResAcc.account_id) return;

		setStrictData(toStrictApi(tempResAcc));
	}, [tempResAcc]);

	useEffect(() => {
		if (!show) {
			setTempResAcc({
				...DEFAULT_TEMP_RES_ACC,
				account_id: accountIdToInteger,
			});
			setShowSideRes(false);
		}
	}, [show, accountIdToInteger, DEFAULT_TEMP_RES_ACC]);

	return (
		<StyledCustomDialog
			{...(width !== undefined ? {$width: width} : {})}
			show={show}
			onClose={onClose}
			$noOverflow={noOverflow}
			$noPadding={noPadding}
			{...maxHeight !== undefined ? {$maxHeight: maxHeight} : {}}
		>
			<Layout direction="column" fullWidth gap="none" nowrap style={{position: 'relative'}}>
				<StyledResDialogHeader
					$noPaddingHeader={noPaddingHeader}
					style={{
						padding: '10px 16px 0 16px',
						position: 'sticky',
						top: 0,
						background: 'white',
						zIndex: 1,
					}}
				>
					<Layout nowrap gap="sm">
						<Text variant="h1">{title}</Text>
						<Divider vertical />
						<Layout alignItems="center">
							<Text variant="body">{isAccountName ? accountName : ''}</Text>
						</Layout>
					</Layout>
					<div>
						<IconButton onClick={onClose} aria-label={t('Close dialog')}>
							<CloseIcon />
						</IconButton>
					</div>
				</StyledResDialogHeader>
				<Divider />
				<StyledResDialogContent
					$flexDirection="row"
					style={{
						width: '100%',
						position: 'sticky',
						top: 46,
						background: 'white',
						zIndex: 1,
					}}
				>
					<MainResContent
						tempResAcc={tempResAcc}
						onChange={handleChange}
						onShowSideRes={onShowSideRes}
						onHideSideRes={onHideSideRes}
						showSideRes={showSideRes}
						downloadReskonto={handleDownloadReskonto}
						billingGroups={billingGroups}
						clientIds={clientIds}
					/>
				</StyledResDialogContent>
				<Divider />
				<Layout direction="row" nowrap fullWidth gap="none">
					{showSideRes && (
						<>
							<Divider vertical />
							<StyledResDialogContent
								$flexDirection="column"
								style={{width: '100%'}}
							>
								<TransferList
									selected={tempResAcc.includes ?? []}
									onChange={handleTransferList}
								/>
							</StyledResDialogContent>
						</>
					)}
				</Layout>
			</Layout>
		</StyledCustomDialog>
	);
};

export default VismaResAccDialog;